import React, { useState } from 'react';
import { ChevronDownIcon, ChevronRightIcon, LockClosedIcon } from '@heroicons/react/24/outline';
import CodeBlock from '../CodeBlock';

const ApiEndpointCard = ({ endpoint }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getMethodClass = (method) => {
    return `http-method-${method.toLowerCase()}`;
  };

  return (
    <div className="api-endpoint-card">
      <div
        className="api-endpoint-header"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        {isExpanded ? (
          <ChevronDownIcon style={{ width: 18, height: 18, color: 'var(--color-text-secondary)' }} />
        ) : (
          <ChevronRightIcon style={{ width: 18, height: 18, color: 'var(--color-text-secondary)' }} />
        )}
        <span className={`http-method-badge ${getMethodClass(endpoint.method)}`}>
          {endpoint.method}
        </span>
        <span className="api-path">{endpoint.path}</span>
        <span style={{ flex: 1, marginLeft: 'var(--space-md)', color: 'var(--color-text-secondary)', fontSize: '0.9rem' }}>
          {endpoint.summary}
        </span>
        {endpoint.authentication && (
          <span className="auth-required-badge">
            <LockClosedIcon style={{ width: 12, height: 12 }} />
            Auth Required
          </span>
        )}
      </div>

      <div className={`api-endpoint-body ${isExpanded ? 'expanded' : ''}`}>
        {/* Description Section */}
        <div className="api-endpoint-section">
          <p style={{ color: 'var(--color-text-secondary)', lineHeight: '1.6' }}>
            {endpoint.description}
          </p>
        </div>

        {/* Path Parameters */}
        {endpoint.pathParams && endpoint.pathParams.length > 0 && (
          <div className="api-endpoint-section">
            <h4 className="section-title">Path Parameters</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Name</th>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Type</th>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.pathParams.map((param, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                      <td style={{ padding: 'var(--space-sm)', fontFamily: 'var(--font-mono)', fontSize: '0.85rem', color: 'var(--color-accent-primary)' }}>
                        {param.name}
                      </td>
                      <td style={{ padding: 'var(--space-sm)', fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>
                        {param.type}
                      </td>
                      <td style={{ padding: 'var(--space-sm)', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                        {param.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Query Parameters */}
        {endpoint.queryParams && endpoint.queryParams.length > 0 && (
          <div className="api-endpoint-section">
            <h4 className="section-title">Query Parameters</h4>
            <div style={{ overflowX: 'auto' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Name</th>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Type</th>
                    <th style={{ padding: 'var(--space-sm)', textAlign: 'left', color: 'var(--color-text-secondary)', fontSize: '0.85rem' }}>Description</th>
                  </tr>
                </thead>
                <tbody>
                  {endpoint.queryParams.map((param, idx) => (
                    <tr key={idx} style={{ borderBottom: '1px solid var(--color-border-primary)' }}>
                      <td style={{ padding: 'var(--space-sm)', fontFamily: 'var(--font-mono)', fontSize: '0.85rem', color: 'var(--color-accent-primary)' }}>
                        {param.name}
                      </td>
                      <td style={{ padding: 'var(--space-sm)', fontSize: '0.85rem', color: 'var(--color-text-tertiary)' }}>
                        {param.type}
                      </td>
                      <td style={{ padding: 'var(--space-sm)', fontSize: '0.85rem', color: 'var(--color-text-secondary)' }}>
                        {param.description}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Request Body */}
        {endpoint.requestBody && (
          <div className="api-endpoint-section">
            <h4 className="section-title">Request Body</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-sm)' }}>
              Content-Type: <code style={{ fontFamily: 'var(--font-mono)', color: 'var(--color-accent-secondary)' }}>
                {endpoint.requestBody.contentType}
              </code>
            </p>
            <CodeBlock
              code={endpoint.requestBody.example}
              language="json"
              title="Example Request"
            />
          </div>
        )}

        {/* Success Response */}
        {endpoint.responseSuccess && (
          <div className="api-endpoint-section">
            <h4 className="section-title">Success Response</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-sm)' }}>
              Status Code: <code style={{
                fontFamily: 'var(--font-mono)',
                color: 'var(--color-accent-success)',
                background: 'rgba(16, 185, 129, 0.15)',
                padding: '2px 6px',
                borderRadius: '4px'
              }}>
                {endpoint.responseSuccess.statusCode}
              </code>
            </p>
            <CodeBlock
              code={endpoint.responseSuccess.example}
              language="json"
              title="Example Response"
            />
          </div>
        )}

        {/* Error Response */}
        {endpoint.responseError && (
          <div className="api-endpoint-section">
            <h4 className="section-title">Error Response</h4>
            <p style={{ fontSize: '0.85rem', color: 'var(--color-text-tertiary)', marginBottom: 'var(--space-sm)' }}>
              Status Code: <code style={{
                fontFamily: 'var(--font-mono)',
                color: 'var(--color-accent-danger)',
                background: 'rgba(239, 68, 68, 0.15)',
                padding: '2px 6px',
                borderRadius: '4px'
              }}>
                {endpoint.responseError.statusCode}
              </code>
            </p>
            <CodeBlock
              code={endpoint.responseError.example}
              language="json"
              title="Example Error"
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default ApiEndpointCard;
